import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import { apiErrorMessage } from '../../utils/apiError';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  FamilyCreateModal,
  FamilyEditModal,
  FamilyMembersModal,
  FamilyDeleteModal
} from './AdminFamilyModals';
import { 
  IoSearchOutline,
  IoAddOutline,
  IoCreateOutline,
  IoSettingsOutline,
  IoTrashOutline
} from 'react-icons/io5';
import AdminDataTable, { AdminPill } from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import {
  useBulkAction, useRowSelection, runBulk, summarizeBulk, useDebouncedValue, useLatestRequest, useFirstLoad
} from '../../components/admin/useAdminTable';
import './AdminFamiliesPage.css';

const AdminFamiliesPage = () => {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  
  // Filters and search
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Expanded family details
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [familyDetails, setFamilyDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  // Management modals
  const [creating, setCreating] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [managingFamily, setManagingFamily] = useState(null);
  const [deletingFamily, setDeletingFamily] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const debouncedSearch = useDebouncedValue(search);
  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);
  const selection = useRowSelection(`${currentPage}|${debouncedSearch}|${sortBy}|${sortOrder}`);
  const bulk = useBulkAction((_result, text) => {
    selection.clear();
    setSuccessMessage(text);
    refreshAfterChange();
  });

  const loadFamilies = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      const response = await adminApi.getFamilies({
        page: currentPage,
        limit: 20,
        search: debouncedSearch,
        sortBy,
        sortOrder
      });
      if (!request.isCurrent()) return;
      setFamilies(response.data.families || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Load families error:', error);
      setError(apiErrorMessage(error, 'Failed to load families'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => { loadFamilies(); }, [loadFamilies]);

  const loadFamilyDetails = async (familyId, { force = false } = {}) => {
    if (familyDetails[familyId] && !force) {
      return; // Already loaded
    }

    try {
      setLoadingDetails(prev => ({ ...prev, [familyId]: true }));
      const response = await adminApi.get(`/admin/families/${familyId}`);
      setFamilyDetails(prev => ({
        ...prev,
        [familyId]: response.data.family
      }));
    } catch (error) {
      console.error('Load family details error:', error);
      setError('Failed to load family details');
    } finally {
      setLoadingDetails(prev => ({ ...prev, [familyId]: false }));
    }
  };

  const handleExpandFamily = async (familyId) => {
    if (expandedFamily === familyId) {
      setExpandedFamily(null);
    } else {
      setExpandedFamily(familyId);
      await loadFamilyDetails(familyId);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  // Membership changes alter the member and reader lists, so the cached detail
  // for the expanded row has to go or it will show pre-change counts.
  const refreshAfterChange = () => {
    setFamilyDetails({});
    loadFamilies();
    // The expanded row would otherwise render empty until collapsed and reopened.
    if (expandedFamily) {
      loadFamilyDetails(expandedFamily, { force: true });
    }
  };

  const closeModals = () => {
    setCreating(false);
    setEditingFamily(null);
    setManagingFamily(null);
    setDeletingFamily(null);
  };

  const handleModalSaved = (message) => {
    closeModals();
    setSuccessMessage(message);
    refreshAfterChange();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const askFamilyBulk = (rows, active) => {
    const wanted = rows.filter((row) => active ? row.isActive === false : row.isActive !== false);
    if (wanted.length === 0) {
      setSuccessMessage(active ? 'None of the selected families are inactive.' : 'None of the selected families are active.');
      return;
    }
    bulk.ask({
      title: `${active ? 'Reactivate' : 'Deactivate'} selected families`,
      confirmLabel: active ? 'Reactivate' : 'Deactivate',
      danger: !active,
      body: (
        <p className="admin-modal-hint">
          {active
            ? 'These families will accept members again.'
            : 'Inactive families stay in the data, but members can no longer use them.'}{' '}
          This applies to {wanted.length} famil{wanted.length === 1 ? 'y' : 'ies'}.
        </p>
      ),
      run: () => runBulk(wanted, (row) => adminApi.patch(`/admin/families/${row.id}`, { isActive: active })),
      summarize: (result) => summarizeBulk(active ? 'Reactivated' : 'Deactivated', 'family', result, 'families')
    });
  };

  if (firstLoad) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading families..." />
      </div>
    );
  }

  return (
    <div className="admin-families-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Family Groups</h1>
          <p className="admin-page-description">
            Manage and view all family groups, their members, and readers
          </p>
        </div>
        <button className="family-new-btn" onClick={() => setCreating(true)}>
          <IoAddOutline /> New Family
        </button>
      </div>

      {successMessage && (
        <div className="family-success-banner">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="admin-card">
        <div className="families-controls">
          <div className="search-box">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              placeholder="Search families by name or invite code..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          {/* Column headers sort on desktop; cards on narrow screens have no headers */}
          <div className="sort-controls admin-sort-mobile">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => handleSortChange(e.target.value)}
              className="sort-select"
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="memberCount">Member Count</option>
              <option value="readerCount">Reader Count</option>
            </select>
            <button 
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className="sort-order-btn"
            >
              {sortOrder === 'ASC' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      <AdminDataTable
        title={`Families (${pagination.totalFamilies || 0})`}
        rows={families}
        getRowLabel={(family) => family.name}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={loadFamilies}
        emptyTitle="No families found"
        emptyMessage="No family groups match your search criteria."
        sort={{ key: sortBy, order: sortOrder, onChange: handleSortChange }}
        expandedId={expandedFamily}
        onToggleExpand={handleExpandFamily}
        renderExpanded={(family) => (
          <div className="family-details">
            {loadingDetails[family.id] ? (
              <div className="details-loading">
                <LoadingSpinner size="small" message="Loading details..." />
              </div>
            ) : familyDetails[family.id] ? (
              <FamilyDetailsView family={familyDetails[family.id]} />
            ) : null}
          </div>
        )}
        pagination={{
          page: currentPage,
          totalPages: pagination.totalPages,
          hasPrev: pagination.hasPrevPage,
          hasNext: pagination.hasNextPage,
          onPageChange: setCurrentPage
        }}
        bulkActions={[
          { label: 'Deactivate', onClick: (rows) => askFamilyBulk(rows, false) },
          { label: 'Reactivate', danger: false, onClick: (rows) => askFamilyBulk(rows, true) }
        ]}
        columns={[
          {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            render: (family) => (
              <span className="admin-cell-strong">
                {family.name}
                {family.isActive === false && <span className="admin-cell-sub"><AdminPill tone="danger">Inactive</AdminPill></span>}
              </span>
            )
          },
          { key: 'members', header: 'Members', sortKey: 'memberCount', render: (family) => family.memberCount },
          { key: 'readers', header: 'Active readers', sortKey: 'readerCount', render: (family) => family.activeReaderCount },
          { key: 'invite', header: 'Invite code', render: (family) => family.inviteCode },
          { key: 'created', header: 'Created', sortKey: 'createdAt', render: (family) => formatDate(family.createdAt) }
        ]}
        renderActions={(family) => (
          <>
            <button type="button" onClick={() => setManagingFamily(family)} title="Add or remove members">
              <IoSettingsOutline /> Members
            </button>
            <button type="button" onClick={() => setEditingFamily(family)} title="Edit family">
              <IoCreateOutline /> Edit
            </button>
            <button type="button" className="is-danger" onClick={() => setDeletingFamily(family)} title="Delete family">
              <IoTrashOutline /> Delete
            </button>
          </>
        )}
      />
      <BulkActionHost bulk={bulk} />

      {creating && (
        <FamilyCreateModal onClose={closeModals} onSaved={handleModalSaved} />
      )}

      {editingFamily && (
        <FamilyEditModal
          family={editingFamily}
          onClose={closeModals}
          onSaved={handleModalSaved}
        />
      )}

      {managingFamily && (
        <FamilyMembersModal
          family={managingFamily}
          onClose={closeModals}
          onChanged={refreshAfterChange}
        />
      )}

      {deletingFamily && (
        <FamilyDeleteModal
          family={deletingFamily}
          onClose={closeModals}
          onDeleted={handleModalSaved}
        />
      )}
    </div>
  );
};

const FamilyDetailsView = ({ family }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="family-details-content">
      <div className="details-section">
        <h4>Family Information</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Name:</label>
            <span>{family.name}</span>
          </div>
          <div className="detail-item">
            <label>Invite Code:</label>
            <span className="invite-code">{family.inviteCode}</span>
          </div>
          <div className="detail-item">
            <label>Created:</label>
            <span>{formatDate(family.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>Creator:</label>
            <span>{family.creator?.name} ({family.creator?.email})</span>
          </div>
          <div className="detail-item">
            <label>Max Members:</label>
            <span>{family.maxMembers}</span>
          </div>
          <div className="detail-item">
            <label>Active:</label>
            <span className={family.isActive ? 'status-active' : 'status-inactive'}>
              {family.isActive ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4>Members ({family.memberCount})</h4>
        <div className="members-list">
          {family.members?.map((member) => (
            <div key={member.id} className="member-item">
              <div className="member-info">
                <strong>{member.name}</strong>
                <span className="member-email">{member.email}</span>
              </div>
              <div className="member-details">
                <span className="member-setting">
                  Competitive: {member.competitiveMode ? 'On' : 'Off'}
                </span>
                <span className="member-setting">
                  Bible: {member.bibleTranslation}
                </span>
                <span className="member-setting">
                  Joined: {formatDate(member.createdAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="details-section">
        <h4>Readers ({family.readerCount})</h4>
        <div className="readers-list">
          {family.readers?.map((reader) => (
            <div key={reader.id} className="reader-item">
              <div className="reader-info">
                <strong>{reader.name}</strong>
                {reader.isPrimary && <span className="primary-badge">Primary</span>}
                <span className="reader-owner">({reader.user?.name})</span>
              </div>
              <div className="reader-details">
                <span className="reader-stat">
                  Age: {reader.age || 'Not set'}
                </span>
                <span className="reader-stat">
                  Gender: {reader.gender || 'Not set'}
                </span>
                <span className="reader-stat">
                  Reports: {reader.reportCount || 0}
                </span>
                <span className={`reader-status ${reader.isActive ? 'active' : 'inactive'}`}>
                  {reader.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="details-section">
        <h4>Statistics</h4>
        <div className="stats-grid">
          <div className="stat-item">
            <label>Total Members:</label>
            <span>{family.memberCount}</span>
          </div>
          <div className="stat-item">
            <label>Total Readers:</label>
            <span>{family.readerCount}</span>
          </div>
          <div className="stat-item">
            <label>Active Readers:</label>
            <span>{family.activeReaderCount}</span>
          </div>
          <div className="stat-item">
            <label>Total Reports:</label>
            <span>{family.totalReports}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFamiliesPage;
